import { ALL_ADMIN_MENU_SECTIONS, getAdminPageOwnerPerspective } from "../constants/adminWorkspaceMenus";
import { PERSPECTIVE_IDS } from "../constants/perspectiveIds";
import { stashAdminDataSourcesTypeFilter } from "../components/midpoint/admin/realEstateSources/adminDataSourcesFilter";

import {
  DATA_FLOW_SUB_PAGE_PARENTS,
  readDataFlowPageFromHash,
} from "./dataFlowHashRoutes";
import { readDataSourceDetailFromHash } from "./dataSourceDetailRoutes";

export const ADMIN_URL_PARAM = "admin";

function pageSlugForItem(sectionId, itemId) {
  const sectionPrefix = sectionId.split("-")[0];
  const prefixed = `${sectionPrefix}-`;
  if (itemId.startsWith(prefixed)) {
    return itemId.slice(prefixed.length);
  }
  return itemId;
}

function perspectiveUrlValue(perspectiveId) {
  if (perspectiveId === PERSPECTIVE_IDS.REAL_ESTATE) {
    return "real-estate";
  }
  return perspectiveId || PERSPECTIVE_IDS.ADMINISTRATION;
}

function buildRegistry() {
  const pathToPageId = {};
  const pageIdToPath = {};

  for (const section of ALL_ADMIN_MENU_SECTIONS) {
    for (const item of section.items || []) {
      const slug = pageSlugForItem(section.id, item.id);
      const path = `${section.id}/${slug}`;
      pathToPageId[path] = item.id;
      pageIdToPath[item.id] = path;
    }
  }

  return { pathToPageId, pageIdToPath };
}

const REGISTRY = buildRegistry();

const ADMIN_PAGE_PATH_ALIASES = {
  "real-estate-source-controls": "data-ops/sources",
};

const ADMIN_PATH_PAGE_ALIASES = {
  "data-ops/sources": "real-estate-source-controls",
};

/** Retired admin paths → replacement page (Vehicle Sources merged into Data Sources). */
const LEGACY_ADMIN_PATH_REDIRECTS = {
  "vehicle-market/sources": {
    pageId: "real-estate-source-controls",
    prepare: () => stashAdminDataSourcesTypeFilter("vehicle_market"),
  },
  "vehicle-market/ingestion-runs": {
    pageId: "ingestion-logs",
  },
};

function normalizeAdminPath(adminPath) {
  return String(adminPath || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

function getLegacyAdminPathRedirect(normalizedPath) {
  return LEGACY_ADMIN_PATH_REDIRECTS[normalizedPath] || null;
}

/** Detail/create/edit routes use hash for entity ids; query param maps to parent list page. */
const ADMIN_SUB_PAGE_PARENTS = {
  ...DATA_FLOW_SUB_PAGE_PARENTS,
};

function buildTitleRegistry() {
  const pageIdToTitle = {};

  for (const section of ALL_ADMIN_MENU_SECTIONS) {
    for (const item of section.items || []) {
      pageIdToTitle[item.id] = item.label;
    }
  }

  return pageIdToTitle;
}

const TITLE_REGISTRY = buildTitleRegistry();

export function getAdminPageTitle(pageId) {
  if (!pageId) return null;
  return TITLE_REGISTRY[pageId] || null;
}

export function resolveAdminPageTitleFromUrl(search = "") {
  const pageId = readAdminPageFromUrl(search);
  if (!pageId) {
    const params = new URLSearchParams(search || "");
    if (params.get("perspective") === "administration") {
      return "Admin";
    }
    return null;
  }
  return getAdminPageTitle(pageId) || pageId;
}

export function resolveAdminPageIdFromPath(adminPath) {
  const normalized = normalizeAdminPath(adminPath);
  if (!normalized) return null;
  const legacy = getLegacyAdminPathRedirect(normalized);
  if (legacy) return legacy.pageId;
  const pathAlias = ADMIN_PATH_PAGE_ALIASES[normalized];
  if (pathAlias) return pathAlias;
  return REGISTRY.pathToPageId[normalized] || null;
}

export function buildAdminPagePath(pageId) {
  if (!pageId) return null;
  const pathAlias = ADMIN_PAGE_PATH_ALIASES[pageId];
  if (pathAlias) return pathAlias;
  return REGISTRY.pageIdToPath[pageId] || REGISTRY.pageIdToPath[ADMIN_SUB_PAGE_PARENTS[pageId]] || null;
}

export function readAdminPageFromHash(hash = "") {
  if (readDataSourceDetailFromHash(hash)) {
    return "real-estate-source-controls";
  }
  return readDataFlowPageFromHash(hash);
}

export function resolveActiveAdminPage(search = "", hash = "") {
  return readAdminPageFromHash(hash) || readAdminPageFromUrl(search);
}

export function readAdminPageFromUrl(search = "") {
  const params = new URLSearchParams(search || window.location.search);
  const adminPath = params.get(ADMIN_URL_PARAM)?.trim();
  if (!adminPath) return null;
  const normalized = normalizeAdminPath(adminPath);
  const legacy = getLegacyAdminPathRedirect(normalized);
  if (legacy) {
    legacy.prepare?.();
    writeAdminPageToUrl(legacy.pageId, { replace: true });
    return legacy.pageId;
  }
  return resolveAdminPageIdFromPath(adminPath);
}

export function buildAdminPageDirectLink(pageId, { origin = window.location.origin } = {}) {
  const path = buildAdminPagePath(pageId);
  if (!path) return null;
  const params = new URLSearchParams();
  params.set("perspective", perspectiveUrlValue(getAdminPageOwnerPerspective(pageId)));
  params.set(ADMIN_URL_PARAM, path);
  return `${origin}/?${params.toString()}`;
}

export function writeAdminPageToUrl(pageId, { replace = true } = {}) {
  const params = new URLSearchParams(window.location.search);
  const path = buildAdminPagePath(pageId);

  if (path) {
    params.set(ADMIN_URL_PARAM, path);
    params.set("perspective", perspectiveUrlValue(getAdminPageOwnerPerspective(pageId)));
  } else if (!pageId) {
    params.delete(ADMIN_URL_PARAM);
  }

  const search = params.toString();
  const previousHash = window.location.hash;
  const hash = readDataSourceDetailFromHash(previousHash) ? "" : previousHash;
  const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${hash}`;

  if (replace) {
    window.history.replaceState({}, "", nextUrl);
  } else {
    window.history.pushState({}, "", nextUrl);
  }

  if (previousHash && hash !== previousHash) {
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }
}
